import { useEffect, useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { useUser, useClerk, Show } from "@clerk/react";
import { X, LogOut, Trash2 } from "lucide-react";
import { DeleteAccountModal } from "@/components/ui/delete-account-modal";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { navItems, creationNavItems, utilityNavItems } from "@/config/navigation";
import { CouchLogo } from "@/components/ui/couch-logo";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setClosing(false);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setVisible(false);
      onClose();
    }, 280);
  }, [onClose]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      {/* Backdrop */}
      <div
        className={cn(
          "absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300",
          closing ? "opacity-0" : "opacity-100",
        )}
        onClick={handleClose}
      />

      {/* Panel */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-[280px] max-w-[85vw] bg-[#faf6ed]/95 backdrop-blur-xl border-r border-white/30 flex flex-col shadow-2xl shadow-black/20 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
          closing ? "-translate-x-full" : "translate-x-0",
        )}
        style={{ animationFillMode: "forwards" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[rgba(45,49,39,0.06)]">
          <div
            className={cn(
              "flex items-center gap-2.5 transition-all duration-500 delay-150",
              closing ? "opacity-0 -translate-x-3" : "opacity-100 translate-x-0",
            )}
          >
            <CouchLogo className="size-7" />
            <span className="text-[15px] font-extrabold text-foreground">CouchStack</span>
          </div>
          <button
            onClick={handleClose}
            className="p-2.5 -mr-1 rounded-xl text-muted-foreground hover:text-foreground hover:bg-[rgba(45,49,39,0.06)] active:scale-90 transition-all duration-150"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 overflow-y-auto no-scrollbar">
          <NavGroup closing={closing} baseDelay={80}>
            {navItems.map((item) => (
              <MobileNavLink key={item.href} item={item} onClose={handleClose} />
            ))}
          </NavGroup>

          <div
            className={cn(
              "px-3 pb-2 pt-3 text-[10px] font-semibold text-[#b5b0a0] uppercase tracking-[0.12em] transition-all duration-300",
              closing ? "opacity-0" : "opacity-100",
            )}
            style={{ transitionDelay: closing ? "0ms" : "200ms" }}
          >
            Create
          </div>
          <NavGroup closing={closing} baseDelay={200}>
            {creationNavItems.map((item) => (
              <MobileNavLink key={item.href} item={item} onClose={handleClose} />
            ))}
          </NavGroup>

          <div
            className={cn(
              "px-3 pb-2 pt-3 text-[10px] font-semibold text-[#b5b0a0] uppercase tracking-[0.12em] transition-all duration-300",
              closing ? "opacity-0" : "opacity-100",
            )}
            style={{ transitionDelay: closing ? "0ms" : "380ms" }}
          >
            Account
          </div>
          <NavGroup closing={closing} baseDelay={400}>
            {utilityNavItems.map((item) => (
              <MobileNavLink key={item.href} item={item} onClose={handleClose} />
            ))}
          </NavGroup>
        </nav>

        <MobileProfileSection onClose={handleClose} />
        <div style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }} />
      </div>
    </div>
  );
}

function NavGroup({
  children,
  closing,
  baseDelay,
}: {
  children: React.ReactNode;
  closing: boolean;
  baseDelay: number;
}) {
  const items = Array.isArray(children) ? children : [children];
  return (
    <div className="space-y-0.5">
      {items.map((child, i) => (
        <div
          key={i}
          className={cn(
            "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
            closing
              ? "opacity-0 -translate-x-3"
              : "opacity-100 translate-x-0",
          )}
          style={{
            transitionDelay: closing ? "0ms" : `${baseDelay + i * 40}ms`,
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}

function MobileProfileSection({ onClose }: { onClose: () => void }) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (!user?.id) return;
    setIsDeleting(true);
    try {
      await api.delete(`/users/${user.id}`);
      await signOut();
      onClose();
    } catch {
      setIsDeleting(false);
    }
  };

  return (
    <Show when="signed-in">
      <div className="p-3 border-t border-[rgba(45,49,39,0.06)]">
        <div className="flex items-center gap-3 px-2 py-2 mb-1">
          <img
            src={user?.imageUrl}
            alt={user?.fullName || "Profile"}
            className="size-9 rounded-xl ring-1 ring-white/60 shadow-sm"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#2d3127] truncate">
              {user?.fullName || user?.username}
            </p>
            <p className="text-[11px] text-[#b5b0a0] truncate">
              {user?.primaryEmailAddress?.emailAddress}
            </p>
          </div>
        </div>
        <button
          onClick={() => { signOut(); onClose(); }}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-[14px] font-medium text-[#8a8570] hover:text-red-500 hover:bg-red-50/60 active:bg-red-100/60 transition-all duration-200"
        >
          <LogOut className="size-5" strokeWidth={1.8} />
          Sign out
        </button>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-[14px] font-medium text-[#8a8570] hover:text-red-500 hover:bg-red-50/60 active:bg-red-100/60 transition-all duration-200"
        >
          <Trash2 className="size-5" strokeWidth={1.8} />
          Delete Account
        </button>
        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
          isDeleting={isDeleting}
        />
      </div>
    </Show>
  );
}

function MobileNavLink({ item, onClose }: { item: (typeof navItems)[0]; onClose: () => void }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.href}
      end={item.href === "/"}
      onClick={onClose}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-[14px] font-medium transition-all duration-200",
          isActive
            ? "text-foreground bg-white/70 shadow-sm border border-white/60"
            : "text-muted-foreground active:bg-white/40 active:scale-[0.98]",
        )
      }
    >
      <Icon className="size-5" strokeWidth={1.8} />
      {item.label}
    </NavLink>
  );
}
